import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'antd';
import PropTypes from 'prop-types';
import Input from '../../../../components/elements/input/Input';
import { createAdmin } from '../../api/adminsApi';
import {
  ADMIN_PIN_PATTERN,
  ADMIN_USERNAME_PATTERN,
  buildAdminUsername,
  normalizeAdminUsername,
  onlyPinDigits,
} from '../../utils/adminUsername';

const emptyForm = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  pin: '',
};

const CreateAdminModal = ({ isOpen, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  // Dok admin ne izmeni korisnicko ime rucno, ono se gradi iz imena i prezimena.
  const [isUsernameEdited, setIsUsernameEdited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setIsUsernameEdited(false);
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'username') {
      setIsUsernameEdited(true);
      setForm((prev) => ({ ...prev, username: value }));
      return;
    }

    if (name === 'pin') {
      setForm((prev) => ({ ...prev, pin: onlyPinDigits(value) }));
      return;
    }

    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (!isUsernameEdited) {
        next.username = buildAdminUsername(next.firstName, next.lastName);
      }
      return next;
    });
  };

  const handleOk = useCallback(async () => {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const username = normalizeAdminUsername(form.username);

    const email = form.email.trim().toLowerCase();

    if (!firstName || !lastName || !username || !form.pin) {
      setError('Sva polja osim emaila su obavezna.');
      return;
    }
    if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Email nije ispravan.');
      return;
    }
    if (!ADMIN_USERNAME_PATTERN.test(username)) {
      setError(
        'Korisničko ime mora biti u obliku ime.prezime (mala slova bez kvačica, cifre i crtice).'
      );
      return;
    }
    if (!ADMIN_PIN_PATTERN.test(form.pin)) {
      setError('PIN mora imati tačno 5 cifara.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const response = await createAdmin({
      firstName,
      lastName,
      username,
      email: email || undefined,
      pin: form.pin,
    });

    if (!response.ok) {
      setError(response.message);
      setLoading(false);
      return;
    }

    setSuccessMessage(
      `${response.message} Korisničko ime: @${response.data?.username || username}`
    );
    setDone(true);
    onSaved?.();

    setTimeout(() => {
      setDone(false);
      onClose();
    }, 1500);

    setLoading(false);
  }, [form, onClose, onSaved]);

  return (
    <Modal
      className="create_user_modal"
      title="Dodaj admina"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '190px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Gotovo"
              className="check-icon"
            />
          ) : (
            'Dodaj admina'
          )}
        </Button>,
      ]}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <Input
          required
          label="Ime"
          id="adminFirstName"
          name="firstName"
          placeholder="Ime"
          value={form.firstName}
          onChange={handleChange}
        />
        <Input
          required
          label="Prezime"
          id="adminLastName"
          name="lastName"
          placeholder="Prezime"
          value={form.lastName}
          onChange={handleChange}
        />
        <Input
          required
          label="Korisničko ime (ime.prezime)"
          id="adminUsername"
          name="username"
          placeholder="Korisničko ime (ime.prezime)"
          autoComplete="off"
          value={form.username}
          onChange={handleChange}
        />
        {/* Opciono; admin ga nigde ne koristi (ni za prijavu ni za reset PIN-a). */}
        <Input
          label="Email (opciono)"
          id="adminEmail"
          name="email"
          type="email"
          placeholder="Email (opciono)"
          autoComplete="off"
          value={form.email}
          onChange={handleChange}
        />
        <Input
          required
          label="PIN (5 cifara)"
          id="adminPin"
          name="pin"
          type="password"
          inputMode="numeric"
          placeholder="PIN (5 cifara)"
          autoComplete="new-password"
          value={form.pin}
          onChange={handleChange}
        />
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

CreateAdminModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};

export default CreateAdminModal;
